
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { AlertErrorCode, CommonErrorCode, SchedulingErrorCode, WebClientErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ExtractEncryptionState, IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { RefreshSelfServiceScheduleDialog } from '~src/extractTask/components/extractRefreshDialog/RefreshSelfServiceScheduleDialog';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { WorkbookContentType } from '~src/spacesPlatform/models/ContentTypes';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ContentUrlForItem } from '~src/spacesPlatform/services/ContentUrl';
import { IWorkbookBase, IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';

import { CreateDatasourceExtractTaskActionService } from './CreateDatasourceExtractTaskAction';
import { CreateExtractTaskActionService, CreateExtractTasksMode } from './CreateExtractTaskAction';

export namespace CreateWorkbookExtractTaskActionService {
  export function forWorkbook(workbook: IWorkbookBase, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const workbookSet = Set.singleton(workbook, (wb: IWorkbookInfo) => wb.id);
    return forWorkbooks(workbookSet, allowedMode);
  }

  export function forWorkbooks(workbooks: Set<IWorkbookBase>, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
      return RefreshSelfServiceScheduleDialog.open(CreateExtractTaskActionService.getRefreshSSSDialogParams(workbooks.toArray()));
    }
    const scheduleOnServerPrompt = (workbooks.size() === 1)
        ? Messages.schedulesLabelPromptWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name) })
        : Messages.schedulesLabelPromptWorkbooks({ COUNT_LOC: L10n.localizedCount(workbooks.size()) });
    const scheduleOnClientPrompt = '';

    const refreshNowPrompt =
      Messages.extractTasksRefreshNowOnWorkbooksPrompt({
        WORKBOOK_COUNT: workbooks.size(),
        WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name),
        WORKBOOK_COUNT_LOC: L10n.localizedCount(workbooks.size()),
      });

    return CreateExtractTaskActionService.execute(
      workbooks,
      scheduleOnServerPrompt,
      scheduleOnClientPrompt,
      (ids: string[], scheduleId: string, type: string) =>
        Workbooks.createExtractTask(ids, scheduleId, type),
      () => Promise.resolve({}),
      refreshNowPrompt,
      (ids: string[], type: string) => Workbooks.refreshExtracts(ids, type),
      (schedule: ISchedule) => workbookErrorDetailFactory(schedule),
      allowedMode
    );
  }

  export function createExtractsForWorkbooks(workbook: INamedItemInfo, datasources: Set<IDataSourceBase>,
                  encryptionState: ExtractEncryptionState): Promise<IResult> {
    return Workbooks.createExtracts([].concat(workbook.id), datasources.keys(), encryptionState === ExtractEncryptionState.encrypted)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractCreationTasksSuccess),
            error: standardActionErrorMsg(Messages.extractCreationTasksError),
          },
          (errorItem: IErrorItem<IDataSourceBase>) => CreateDatasourceExtractTaskActionService.datasourceErrorDetailFactory(null)(errorItem)
        )
      );
  }

  export function workbookErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IWorkbookBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: ContentUrlForItem(errorItem.item, WorkbookContentType),
        errorMessage: workbookErrorMessageFor(errorItem, schedule)
      };
    };
  }

  export function workbookErrorMessageFor(errorItem: IErrorItem<IWorkbookBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return Messages.extractTasksWorkbookNoExtract();
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return Messages.extractTasksWorkbookNoIncrementalExtract();
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return Messages.extractTasksRefreshAlreadyExistsForWorkbook({
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.extractTasksWorkbookErrorInsufficientPermissions();
      case AlertErrorCode.AlertConnectionlessFailure:
        return Messages.credentialMissedOrWrong();
      case AlertErrorCode.AlertOperationCanceled:
        return Messages.jobCancelled();
      case SchedulingErrorCode.SchedulingNotAllowedInPersonalSpace:
        return Messages.extractTasksRefreshNotAllowedInPersonalSpaceForWorkbook();
    }
    return CreateExtractTaskActionService.extractErrorMessageFor(errorItem, schedule);
  }
}
